'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { DrawerFloatingContext } from './context/drawer-floating.context';
import { DrawerRootContext } from './context/drawer-root.context';
import { drawerContentRecipe } from './styles/drawer-content.styles';
import type { DrawerContentPropsInterface } from './types/drawer-content.types';

/**
 * @since 10.22.0
 */
export const DrawerContent = forwardRef<HTMLDivElement, DrawerContentPropsInterface>(
  (props: DrawerContentPropsInterface, forwardedRef) => {
    const { drawer, placement } = useContext(DrawerRootContext);
    const floating = useContext(DrawerFloatingContext);
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      ...rest
    } = props;

    const styles = css(
      drawerContentRecipe.raw({ open: floating.status === 'open', placement }),
      cssProp,
    );

    /*
     * Omitting `floating` from the order keeps the dialog container out of the
     * tab order. Floating UI then falls back to `tabindex="0"` on the container
     * only when the drawer has no tabbable content of its own, and initial focus
     * lands on the first tabbable element (usually `Drawer.CloseButton`).
     */
    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['content'],
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([drawer.refs.setFloating, forwardedRef]);

    const Component = asChild ? Slot : 'div';

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <Component
          ref={mergedRefs}
          aria-modal="true"
          aria-labelledby={drawer.labelId}
          aria-describedby={drawer.descriptionId}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Drawer.Content"
          className={cx('c11n', 'c11n_drawer__content', styles, className)}
          {...floating.getFloatingProps(rest)}
          // Override id when using multi drawer trigger id
          {...(drawer.multiDrawerTriggerId ? { id: drawer.multiDrawerTriggerId } : {})}
        >
          {children}
        </Component>
      </FloatingFocusManager>
    );
  },
);

DrawerContent.displayName = 'Drawer.Content';
